import type { QueryClient } from '@tanstack/react-query';

import { REALTIME_RECONNECTED, REALTIME_TOPIC_REVOKED, type RealtimeManager } from '@/shared/realtime';

import { wikiKeys, type PageDetail } from './api';

/** Realtime event names of the wiki module (backend `WikiEvents`). */
export const WIKI_EVENTS = {
  pageUpdated: 'wiki.page-updated',
  pageDeleted: 'wiki.page-deleted',
  treeChanged: 'wiki.tree-changed',
  spaceUpdated: 'wiki.space-updated',
  spaceDeleted: 'wiki.space-deleted',
  commentCreated: 'wiki.comment-created',
  commentUpdated: 'wiki.comment-updated',
  commentDeleted: 'wiki.comment-deleted',
} as const;

export interface PageChangedPayload {
  pageId: string;
  spaceId: string;
  version: number;
  actorId: string | null;
  reason: string;
}

export interface SpaceChangedPayload {
  spaceId: string;
  actorId: string | null;
  reason: string;
}

export interface CommentPayload {
  pageId: string;
  commentId: string;
  authorId: string;
}

/** Reasons of `wiki.page-updated` that bring new content: an open reader shows a banner instead of swapping the text. */
export const CONTENT_REASONS = new Set(['published', 'restored']);

/** `reason` of `wiki.page-updated` when a page comes back from the trash (the page is readable again). */
export const RESTORED_FROM_TRASH = 'untrashed';

/** Pages deleted while this tab was open (the reader shows "deleted" instead of an error). */
const deletedPages = new Set<string>();

export const isPageDeleted = (id: string) => deletedPages.has(id);

/** Which cached queries an event makes stale, and how (pure, unit tested). */
export function staleQueriesFor(
  type: string,
  payload: Partial<PageChangedPayload & SpaceChangedPayload & CommentPayload> | null,
): { refetch: (readonly unknown[])[]; markStale: (readonly unknown[])[] } {
  const refetch: (readonly unknown[])[] = [];
  const markStale: (readonly unknown[])[] = [];
  const spaceId = payload?.spaceId;
  const pageId = payload?.pageId;
  switch (type) {
    case WIKI_EVENTS.treeChanged:
      if (spaceId) refetch.push(wikiKeys.tree(spaceId), wikiKeys.space(spaceId), wikiKeys.trash(spaceId));
      refetch.push(wikiKeys.lists(), wikiKeys.spaces());
      break;
    case WIKI_EVENTS.pageUpdated:
      if (spaceId) refetch.push(wikiKeys.tree(spaceId));
      refetch.push(wikiKeys.lists());
      if (pageId) {
        refetch.push(wikiKeys.versions(pageId));
        // New content: the open reader offers to show it (banner); other changes (title, icon…) apply at once.
        (CONTENT_REASONS.has(payload.reason ?? '') ? markStale : refetch).push(wikiKeys.page(pageId));
      }
      break;
    case WIKI_EVENTS.pageDeleted:
      if (spaceId) refetch.push(wikiKeys.tree(spaceId), wikiKeys.space(spaceId), wikiKeys.trash(spaceId));
      refetch.push(wikiKeys.lists(), wikiKeys.spaces());
      break;
    case WIKI_EVENTS.spaceUpdated:
      if (spaceId) refetch.push(wikiKeys.space(spaceId), wikiKeys.tree(spaceId));
      refetch.push(wikiKeys.spaces(), ['wiki', 'group-space']);
      break;
    case WIKI_EVENTS.spaceDeleted:
      if (spaceId) markStale.push(wikiKeys.space(spaceId), wikiKeys.tree(spaceId));
      markStale.push(wikiKeys.lists());
      refetch.push(wikiKeys.spaces());
      break;
    case WIKI_EVENTS.commentCreated:
    case WIKI_EVENTS.commentUpdated:
    case WIKI_EVENTS.commentDeleted:
      if (pageId) refetch.push(wikiKeys.comments(pageId));
      break;
    default:
      break;
  }
  return { refetch, markStale };
}

/**
 * What a revoked topic (`realtime.topic-revoked`: the reader lost access, e.g. removed from the group) does to the
 * caches (pure, unit tested). `reset` drops the cached data, so an open reader stops showing the content (and its
 * edit and comment actions) and refetches into its "not found or no access" state; `refetch` keeps data until the
 * refetch answers.
 */
export function revokedQueriesFor(topic: string | undefined): {
  reset: (readonly unknown[])[];
  refetch: (readonly unknown[])[];
} {
  const reset: (readonly unknown[])[] = [];
  const refetch: (readonly unknown[])[] = [];
  if (topic?.startsWith('wiki-page:')) {
    const pageId = topic.slice('wiki-page:'.length);
    reset.push(
      wikiKeys.page(pageId),
      wikiKeys.draft(pageId),
      wikiKeys.comments(pageId),
      wikiKeys.versions(pageId),
    );
  } else if (topic?.startsWith('wiki-space:')) {
    const spaceId = topic.slice('wiki-space:'.length);
    reset.push(wikiKeys.space(spaceId), wikiKeys.tree(spaceId));
    refetch.push(wikiKeys.spaces(), wikiKeys.lists(), ['wiki', 'group-space']);
  }
  return { reset, refetch };
}

/**
 * Keeps the wiki caches live: `wiki.*` events (topics `wiki-space:<id>` of an open tree and `wiki-page:<id>` of an
 * open page) invalidate what they affect; a revoked topic drops what the reader may no longer see; a reconnect
 * refetches everything (events may have been missed).
 */
export function registerWikiRealtime(realtime: RealtimeManager, queryClient: QueryClient): () => void {
  const offEvents = realtime.on<Record<string, unknown> | null>('wiki.*', (payload, envelope) => {
    const data = payload as Partial<PageChangedPayload & SpaceChangedPayload & CommentPayload> | null;
    if (envelope.type === WIKI_EVENTS.pageDeleted && data?.pageId) deletedPages.add(data.pageId);
    if (envelope.type === WIKI_EVENTS.pageUpdated && data?.pageId && data.reason === RESTORED_FROM_TRASH)
      deletedPages.delete(data.pageId);
    if (
      data?.pageId &&
      (envelope.type === WIKI_EVENTS.commentCreated || envelope.type === WIKI_EVENTS.commentDeleted)
    ) {
      // Keep the counter in the page header right without refetching the page (whose content may be newer than
      // what the reader shows). The caller's own comments arrive here too (the topic includes the author).
      const delta = envelope.type === WIKI_EVENTS.commentCreated ? 1 : -1;
      queryClient.setQueryData<PageDetail>(wikiKeys.page(data.pageId), (page) =>
        page ? { ...page, commentCount: Math.max(0, page.commentCount + delta) } : page,
      );
    }
    const { refetch, markStale } = staleQueriesFor(envelope.type, data);
    for (const queryKey of refetch) void queryClient.invalidateQueries({ queryKey });
    for (const queryKey of markStale) void queryClient.invalidateQueries({ queryKey, refetchType: 'none' });
  });
  const offRevoked = realtime.on<{ topic?: string } | null>(REALTIME_TOPIC_REVOKED, (payload) => {
    const { reset, refetch } = revokedQueriesFor(payload?.topic);
    for (const queryKey of reset) void queryClient.resetQueries({ queryKey });
    for (const queryKey of refetch) void queryClient.invalidateQueries({ queryKey });
  });
  const offReconnect = realtime.on(REALTIME_RECONNECTED, () => {
    void queryClient.invalidateQueries({ queryKey: wikiKeys.all });
  });
  return () => {
    offEvents();
    offRevoked();
    offReconnect();
  };
}
